import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RefreshCw, Users, Activity, TrendingUp, DollarSign, BarChart3, PieChart, Calendar, Clock, Trash2, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Cell, LineChart, Line, Pie } from "recharts";
import { useAdminAnalytics } from "@/hooks/useAdminAnalytics";
import { ListSkeleton } from "@/components/LoadingStates";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const AdminAnalytics = () => {
  const [testMode, setTestMode] = useState(false);
  const [deletingTestData, setDeletingTestData] = useState(false);
  const { toast } = useToast();
  
  const { 
    loading, 
    userBehavior, 
    demographics, 
    promoEngagement, 
    advertiserOverview, 
    refreshAnalytics 
  } = useAdminAnalytics(testMode);

  const deleteAllTestData = async () => {
    if (!confirm("Are you sure you want to delete ALL test data? This action cannot be undone.")) {
      return;
    }

    setDeletingTestData(true);
    try {
      // Delete test data from all relevant tables
      await Promise.all([
        supabase.from('usage_logs').delete().eq('is_test', true),
        supabase.from('redemptions').delete().eq('is_test', true),
        supabase.from('points_transactions').delete().eq('is_test', true),
        supabase.from('checkins').delete().eq('is_test', true),
        supabase.from('rewards_catalog').delete().eq('is_test', true),
        supabase.from('ads').delete().eq('is_test', true),
        supabase.from('advertisers').delete().eq('is_test', true),
        supabase.from('daily_messages').delete().eq('is_test', true),
        supabase.from('users').delete().eq('is_test', true)
      ]);

      toast({
        title: "Test data deleted",
        description: "All test data has been successfully removed from the database.",
      });

      // Refresh analytics
      refreshAnalytics();
    } catch (error) {
      console.error('Error deleting test data:', error);
      toast({
        title: "Error",
        description: "Failed to delete test data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeletingTestData(false);
    }
  };

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

  if (loading) {
    return <ListSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Test Mode Banner */}
      {testMode && (
        <Alert className="border-destructive bg-destructive/10">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="font-medium text-destructive">
            🚧 TEST MODE ENABLED - Showing only test data
          </AlertDescription>
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Analytics Dashboard</h1>
          <p className="text-muted-foreground">Comprehensive insights into app performance and user behavior</p>
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={refreshAnalytics} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Test Mode Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Data Mode</CardTitle>
          <CardDescription>Toggle between production and test data</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="test-mode"
              checked={testMode}
              onCheckedChange={setTestMode}
            />
            <Label htmlFor="test-mode">Test Mode</Label>
            <span className="text-sm text-muted-foreground">
              {testMode ? "Showing test data only" : "Showing production data only"}
            </span>
          </div>
          
          {testMode && (
            <Button 
              onClick={deleteAllTestData} 
              variant="destructive" 
              size="sm"
              disabled={deletingTestData}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deletingTestData ? "Deleting..." : "Delete All Test Data"}
            </Button>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="user-behavior" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="user-behavior">User Behavior</TabsTrigger>
          <TabsTrigger value="demographics">Demographics</TabsTrigger>
          <TabsTrigger value="promotions">Promotions</TabsTrigger>
          <TabsTrigger value="advertisers">Advertisers</TabsTrigger>
        </TabsList>

        {/* User Behavior Tab */}
        <TabsContent value="user-behavior" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userBehavior.totalActiveUsers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Daily Check-ins</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userBehavior.dailyCheckins.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Weekly Check-ins</CardTitle>
                <Calendar className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userBehavior.weeklyCheckins.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Last 7 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Wait Time</CardTitle>
                <Clock className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{userBehavior.averageWaitTime} min</div>
                <p className="text-xs text-muted-foreground">All crossings</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Top Crossings</CardTitle>
                <CardDescription>Most popular border crossings (last 30 days)</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={userBehavior.topCrossings}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="crossing" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Check-in Activity</CardTitle>
                <CardDescription>Check-ins by time and day</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="flex items-center gap-2">
                      <span className="w-8 text-xs">{day}</span>
                      <div className="flex gap-1 flex-1">
                        {Array.from({length: 24}, (_, hour) => {
                          const dayData = userBehavior.checkinHeatmap.find(d => d.day === day && d.hour === hour);
                          const intensity = dayData ? Math.min(dayData.count / 10, 1) : 0;
                          return (
                            <div
                              key={hour}
                              className="w-3 h-3 rounded-sm border"
                              style={{
                                backgroundColor: `hsl(var(--primary) / ${intensity})`,
                                borderColor: 'hsl(var(--border))'
                              }}
                              title={`${day} ${hour}:00 - ${dayData?.count || 0} check-ins`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Demographics Tab */}
        <TabsContent value="demographics" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{demographics.totalUsers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Registered users</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Repeat Users</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{demographics.repeatUsers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Multiple check-ins</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">One-time Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{demographics.oneTimeUsers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Single check-in</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Crossing Types</CardTitle>
                <CardDescription>Distribution of lane types used</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={demographics.crossingTypes}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ type, percent }) => `${type} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {demographics.crossingTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>User Retention</CardTitle>
                <CardDescription>Repeat vs one-time users</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={[
                    { name: 'Repeat Users', value: demographics.repeatUsers },
                    { name: 'One-time Users', value: demographics.oneTimeUsers }
                  ]}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Promotions Tab */}
        <TabsContent value="promotions" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Redemptions</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{promoEngagement.totalRedemptions.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All time</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Promotions</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{promoEngagement.totalPromotions.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Points Spent</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{promoEngagement.averagePointsSpent}</div>
                <p className="text-xs text-muted-foreground">Per redemption</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Most Viewed Promotions</CardTitle>
                <CardDescription>Based on impressions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {promoEngagement.mostViewedPromos.slice(0, 5).map((promo, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{index + 1}</Badge>
                        <span className="font-medium">{promo.company_name}</span>
                      </div>
                      <span className="text-muted-foreground">{promo.views.toLocaleString()} views</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Most Redeemed Promotions</CardTitle>
                <CardDescription>Top performing offers</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {promoEngagement.mostRedeemedPromos.slice(0, 5).map((promo, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{index + 1}</Badge>
                        <span className="font-medium">{promo.company_name}</span>
                      </div>
                      <span className="text-muted-foreground">{promo.redemptions.toLocaleString()} redemptions</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Advertisers Tab */}
        <TabsContent value="advertisers" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Advertisers</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{advertiserOverview.totalAdvertisers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">Active advertisers</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Ad Spend</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${advertiserOverview.totalSpend.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All time revenue</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Top Spending Advertisers</CardTitle>
                <CardDescription>By total ad spend</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={advertiserOverview.advertiserSpending.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="company_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip formatter={(value) => [`$${value}`, 'Spend']} />
                    <Bar dataKey="spend" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Impressions vs Engagement</CardTitle>
                <CardDescription>Views vs clicks by advertiser</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={advertiserOverview.impressionsVsRedemptions.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="company_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="impressions" fill="hsl(var(--primary))" name="Impressions" />
                    <Bar dataKey="redemptions" fill="hsl(var(--secondary))" name="Clicks" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAnalytics;